import { PROTECTION_TIER_FAMILIES } from './emotional-impact';
import type {
  AccessibilityPreferences,
  EmotionalFamilyPresentationDefinition,
  ModePresentationDefinition,
  MovementIntensity,
  PresentationElement,
  PresentationPreferences,
} from './presentation-types';
import type { EmotionalFamily } from '../../../domain/emotion/enums';

/**
 * ASCEND V7 — matrice de priorité, formalisée en règles composables plutôt qu'en table
 * exhaustive 9 modes × 36 états (324 lignes). Chaque fonction ci-dessous répond à UNE
 * question de la matrice ; `resolvePresentation.ts` les compose dans l'ordre suivant :
 *
 *   1. sécurité et protection      — jamais contournée, gagne toujours
 *   2. état émotionnel sensible    — atténue et met en avant, ne touche jamais au fonctionnel
 *   3. accessibilité (AGS, réduction de mouvement) — gagne sur l'émotion et les préférences
 *   4. mode de contexte             — porte missionCount / exigence / densité (« ambitions du jour »)
 *   5. préférences utilisateur      — seul mécanisme autorisé à retirer un élément de l'écran
 *   6. ambitions du jour             — c'est la couche « mode » elle-même (4) ; nommée séparément
 *      dans la matrice de lecture pour rappeler que l'exigence du mode ne cède qu'à la protection.
 *
 * RÈGLE STRUCTURELLE ABSOLUE — aucune règle ci-dessous ne modifie missionCount, exigence ou
 * densité : ce sont des propriétés du MODE (couche 4/6), jamais de l'état émotionnel (couche 2).
 * C'est ce qui garantit, structurellement et pas par convention, qu'« Élévation + Épuisé »
 * n'augmente jamais l'exigence : la fonction de la couche 2 n'a tout simplement pas accès à
 * `exigence`/`density` en entrée.
 */

const REDUCED_TIER_FAMILIES = new Set<EmotionalFamily>(
  PROTECTION_TIER_FAMILIES.map((protectionFamily) => protectionFamily),
);

/**
 * Couche 1+2 — sécurité, protection et état émotionnel sensible. Un état actif atténue les
 * éléments listés par sa famille, sauf sous AGS où l'accessibilité (couche 3) annule
 * l'atténuation : l'utilisateur en mode contraste renforcé voit tout au plein contraste.
 */
export function resolveDimmedElements(
  family: EmotionalFamilyPresentationDefinition | null,
  a11y: AccessibilityPreferences,
): readonly PresentationElement[] {
  if (!family || a11y.ags) return [];
  return family.attenuates;
}

/**
 * Couche 1 — protection. Les familles les plus sensibles (vulnérabilité, recentrage, coup dur)
 * mettent en avant le bloc d'intention/lignes directrices — jamais un élément fonctionnel, et
 * jamais en retirant quoi que ce soit d'autre.
 */
export function resolveEmphasizedElements(
  family: EmotionalFamilyPresentationDefinition | null,
): readonly PresentationElement[] {
  if (!family || !REDUCED_TIER_FAMILIES.has(family.family)) return [];
  return ['guideline-panel'];
}

/**
 * Couche 3 — accessibilité. `prefers-reduced-motion` et l'atténuation d'une famille peuvent
 * tous deux réduire le mouvement ; AGS le fige entièrement. L'accessibilité ne fait jamais
 * *plus* de mouvement qu'une famille ne le demande, seulement moins ou autant.
 */
export function resolveMovementIntensity(
  family: EmotionalFamilyPresentationDefinition | null,
  a11y: AccessibilityPreferences,
): MovementIntensity {
  if (a11y.ags) return 'still';
  if (a11y.reducedMotion) return 'still';
  return family?.movementIntensity ?? 'normal';
}

/**
 * Couche 5 — préférences utilisateur. Seul mécanisme autorisé à retirer un élément de
 * l'écran (jamais une émotion, jamais un mode). N'a aucune autorité sur l'accessibilité.
 */
export function resolveHiddenByUser(
  prefs: PresentationPreferences,
): readonly PresentationElement[] {
  const hidden: PresentationElement[] = [];
  if (!prefs.showProgress) hidden.push('progress');
  if (!prefs.showEditorialCards) hidden.push('editorial-cards');
  if (!prefs.showTimes) hidden.push('durations');
  return hidden;
}

/**
 * Couche 4/6 — mode de contexte (« ambitions du jour »). Exigence et densité viennent
 * TOUJOURS du mode, jamais de la famille émotionnelle active.
 */
export function resolveFunctionalProperties(mode: ModePresentationDefinition) {
  return { exigence: mode.exigence, density: mode.density };
}

export interface ResolvedAtmosphere {
  readonly accent: string;
  readonly light: string;
  readonly tone: string;
  readonly heroImageKey: string;
  readonly heroImagePosition: string;
  readonly thoughtKey: string;
  readonly thought: string;
  readonly intentionKey: string;
  readonly intention: string;
  readonly routineKeys: readonly string[];
  readonly routines: readonly string[];
}

/**
 * Couche 2 — présentation. Quand un état est actif, sa famille remplace l'atmosphère du mode
 * (accent, image, lumière, ton, pensée, intention, routines) ; sinon le mode reste seul maître.
 * Ne touche jamais aux propriétés fonctionnelles (voir `resolveFunctionalProperties`).
 */
export function resolveAtmosphere(
  mode: ModePresentationDefinition,
  family: EmotionalFamilyPresentationDefinition | null,
): ResolvedAtmosphere {
  if (!family) {
    return {
      accent: mode.accent,
      light: mode.light,
      tone: mode.tone,
      heroImageKey: mode.heroImageKey,
      heroImagePosition: mode.heroImagePosition,
      thoughtKey: mode.content.thoughtKey,
      thought: mode.content.thought,
      intentionKey: mode.content.intentionKey,
      intention: mode.content.intention,
      routineKeys: mode.content.routineKeys,
      routines: mode.content.routines,
    };
  }
  return {
    accent: family.accent,
    light: family.light,
    tone: family.tone,
    heroImageKey: family.heroImageKey,
    heroImagePosition: family.heroImagePosition,
    thoughtKey: family.thoughtKey,
    thought: family.thought,
    intentionKey: family.intentionKey,
    intention: family.intention,
    routineKeys: family.routineKeys,
    routines: family.routines,
  };
}

/** Couche 4 — classes de portée CSS, dans l'ordre d'application (le mode d'abord, la famille gagne, AGS gagne sur tout). */
export function resolveScopes(
  mode: ModePresentationDefinition,
  family: EmotionalFamilyPresentationDefinition | null,
  a11y: AccessibilityPreferences,
): readonly string[] {
  return [mode.scopeClass, family?.scopeClass, a11y.ags ? 'ags' : undefined].filter(
    (scope): scope is string => Boolean(scope),
  );
}
